import { useMemo, useState } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DayDistribution } from '@texas/api/endpoints/statisticsApi';
import { useTranslation } from 'react-i18next';
import {
  BranchIdentifier,
  getBranchColor,
} from '@texas/utils/helpers/texas/branchHelpers';
import { PaOverviewModal } from '../shared/components/productApprovals/PaOverviewModal';
import { PaTableColumns } from '@texas/components/dashboard/PaTableColumns';
import { FilterOptionType } from '@texas/components/filter/types/types';
import { defaultEmptyFilters } from '@texas/api/endpoints/search/searchPaTypes';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

interface DistributionChartProps {
  data: DayDistribution[];
}

export function DistributionChart({ data }: DistributionChartProps) {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [selectedData, setSelectedData] = useState<{
    productApprovalIds: number[];
    branchIds: number[];
    dayCount: number;
    branchName: string;
  } | null>(null);

  const chartData = useMemo(() => {
    const allBranches = new Set<string>();
    const branchIdentifierMap = new Map<string, string>();

    data.forEach((day) => {
      day.branches.forEach((b) => {
        allBranches.add(b.branchName);
        branchIdentifierMap.set(b.branchName, b.branchIdentifier);
      });
    });

    const branchNames = Array.from(allBranches);
    const labels = data.map(
      (day) => `${day.dayCount} ${day.dayCount === 1 ? 'day' : 'days'}`,
    );

    const datasets = branchNames.map((branchName) => {
      const branchIdentifier = branchIdentifierMap.get(
        branchName,
      ) as BranchIdentifier;
      const branchColor = getBranchColor(branchIdentifier);

      const branchData = data.map((day) => {
        const branch = day.branches.find((b) => b.branchName === branchName);
        return branch ? branch.count : 0;
      });

      const cssVar = branchColor.match(/var\((--chakra-colors-[^)]+)\)/)?.[1];
      let resolvedColor = branchColor;

      if (cssVar) {
        const computedColor = getComputedStyle(document.documentElement)
          .getPropertyValue(
            cssVar.replace('--chakra-colors-', '--chakra-colors-'),
          )
          .trim();

        if (computedColor) {
          resolvedColor = computedColor;
        }
      }

      return {
        label: branchName,
        data: branchData,
        backgroundColor: resolvedColor,
        borderColor: resolvedColor,
        borderWidth: 1,
      };
    });

    return { labels, datasets };
  }, [data]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      onClick: (_event, elements) => {
        if (elements.length > 0) {
          const element = elements[0];
          const datasetIndex = element.datasetIndex;
          const dataIndex = element.index;

          const dayDistribution = data[dataIndex];
          const branchName = chartData.datasets[datasetIndex].label;
          const branchDistribution = dayDistribution.branches.find(
            (b) => b.branchName === branchName,
          );

          if (
            branchDistribution &&
            branchDistribution.productApprovalIds.length > 0
          ) {
            setSelectedData({
              productApprovalIds: branchDistribution.productApprovalIds,
              branchIds: [branchDistribution.branchId],
              dayCount: dayDistribution.dayCount,
              branchName: branchDistribution.branchName,
            });
            onOpen();
          }
        }
      },
      onHover: (event: any, activeElements: any[]) => {
        if (event.native?.target) {
          event.native.target.style.cursor =
            activeElements.length > 0 ? 'pointer' : 'default';
        }
      },
      scales: {
        x: {
          stacked: true,
          title: {
            display: true,
            text: t('widgets.paLeadTimeDistribution.daysFromCreation'),
          },
          ticks: {
            color: (context: any) => {
              const index = context.index;
              const dayCount = data[index]?.dayCount ?? 0;
              return dayCount > 16 ? '#d85772' : '#787878';
            },
          },
        },
        y: {
          stacked: true,
          title: {
            display: true,
            text: t('widgets.paLeadTimeDistribution.numberOfPAs'),
          },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            font: {
              size: 11,
            },
          },
        },
        title: {
          display: true,
          text: t('widgets.paLeadTimeDistribution.chartTitle'),
        },
        tooltip: {
          callbacks: {
            footer: () => {
              return String(t('widgets.paLeadTime.clickToViewDetails'));
            },
            label: (context: any) => {
              const branchName = context.dataset.label;
              const count = context.parsed.y;
              const dayIndex = context.dataIndex;
              const totalForDay = data[dayIndex].totalCount;
              const percentage = ((count / totalForDay) * 100).toFixed(1);

              return `${branchName}: ${count} PAs (${percentage}%)`;
            },
          },
        },
      },
    }),
    [t, data, chartData, onOpen],
  );

  return (
    <>
      <Box height="500px" width="100%">
        <Bar data={chartData} options={options} />
      </Box>

      {selectedData && (
        <PaOverviewModal
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelectedData(null);
          }}
          filters={{
            ...defaultEmptyFilters,
            productApprovalIds: selectedData.productApprovalIds,
            branchIds: selectedData.branchIds,
          }}
          searchProps={{
            filters: [FilterOptionType.Branches],
            searchParams: {
              searchTerm: '',
              sortBy: '',
              sortDesc: false,
              page: 1,
              ...defaultEmptyFilters,
              productApprovalIds: selectedData.productApprovalIds,
              branchIds: selectedData.branchIds,
            },
          }}
          columns={PaTableColumns}
        />
      )}
    </>
  );
}
